import { usePathname, useRouter, useSearchParams } from "next/navigation";
import NProgress from "nprogress";
import { useCallback, useEffect, useState, useTransition } from "react";

const useFilterOptions = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [selectedValues, setSelectedValues] = useState({});
  const [isPending, startTransition] = useTransition();

  // Initialize selected values based on URL params when you reload the page
  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString());
    const values = {};
    params.forEach((value, key) => {
      values[key] = value.split(",");
    });
    setSelectedValues(values);
  }, [searchParams, setSelectedValues]);

  const createQueryString = useCallback(
    (name, value, checked) => {
      const params = new URLSearchParams(searchParams.toString());
      const currentValues = params.get(name)?.split(",") || [];

      if (checked) {
        // Add the new value if checked
        currentValues.push(value);
      } else {
        // Remove the value if unchecked
        const index = currentValues.indexOf(value);
        if (index > -1) {
          currentValues.splice(index, 1);
        }
      }

      // Update the params
      if (currentValues.length > 0) {
        params.set(name, currentValues.join(","));
      } else {
        params.delete(name);
      }
      // Reset to the first page when filtering
      if (params.has("page") && params.get("page") !== "1") {
        params.set("page", "1");
      }
      return params.toString();
    },
    [searchParams]
  );

  const updateQueryParams = useCallback(
    (facetType, value, checked) => {
      const updatedQueryString = createQueryString(facetType, value, checked);
      const newUrl = decodeURIComponent(
        `${pathname}${updatedQueryString ? "?" + updatedQueryString : ""}`
      );
      startTransition(async () => {
        NProgress.start();
        router?.push(newUrl, undefined, { shallow: true });
      });
      if (!updatedQueryString) {
        router.refresh();
      }
    },
    [router, pathname, createQueryString]
  );

  const handleCheckboxChange = useCallback(
    (e, facetType) => {
      const { value, checked } = e.target;
      // Clone the current selectedValues to avoid direct mutation
      let newSelectedValues = { ...selectedValues };

      // Ensure that the specific facetType has an array initialized
      if (!Array.isArray(newSelectedValues[facetType])) {
        newSelectedValues[facetType] = [];
      }

      if (checked) {
        // Add the new value to the selected array for this facetType
        // Only add if it's not already in the array
        if (!newSelectedValues[facetType].includes(value)) {
          newSelectedValues[facetType].push(value);
        }
      } else {
        // Remove the value from the selected array for this facetType
        newSelectedValues[facetType] = newSelectedValues[facetType].filter(
          (option) => option !== value
        );
      }

      // Update the state with the new selected values
      setSelectedValues(newSelectedValues);

      // Update the query parameters
      updateQueryParams(facetType, value, checked);
    },
    [selectedValues, updateQueryParams]
  );

  const updatePriceFilter = useCallback(
    (minSelected, maxSelected, minDefault, maxDefault) => {
      const params = new URLSearchParams(searchParams.toString());

      const isResetToDefault =
        minSelected === minDefault && maxSelected === maxDefault;

      if (isResetToDefault) {
        params.delete("price");
      } else {
        const priceRange = `${minSelected}-${maxSelected}`;
        params.set("price", priceRange);
      }

      const queryString = params.toString();
      const newUrl = decodeURIComponent(
        `${pathname}${queryString ? `?${queryString}` : ""}`
      );

      NProgress.start();
      router.push(newUrl, undefined, { shallow: true });
      router.refresh();
    },
    [searchParams, pathname, router]
  );

  const clearAllFilters = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());

    // List of query parameters that should not be removed
    const preserveParams = ["sort", "term"];

    // Remove only filter-related params (preserve sort and pagination)
    Object.keys(selectedValues).forEach((key) => {
      if (!preserveParams.includes(key)) {
        params.delete(key);
      }
    });

    const newUrl = decodeURIComponent(`${pathname}?${params.toString()}`);
    NProgress.start();
    router?.push(newUrl, undefined, { shallow: true });

    setSelectedValues({}); // Clear selected values in state
    router.refresh(); // Refresh the page
  }, [selectedValues, searchParams, pathname, router]);

  return {
    selectedValues,
    handleCheckboxChange,
    clearAllFilters,
    updatePriceFilter,
    isPending,
  };
};

export default useFilterOptions;
